/**
 * 誌面本文の読み上げ（M11・Web Speech API の `SpeechSynthesis`）。
 *
 * **適合の話ではない。** 読み上げは WCAG の達成基準を 1 つも満たさず、スクリーンリーダー実機確認の
 * 代替にもならない（DECISIONS 2026-08-15）。作る理由は製品価値 — M11 手順 0 の実機確認で、
 * **Edge と Chrome の内蔵読み上げは誌面本文へ届かない**ことが分かっている（見出し行まで）。
 * アクセシビリティツリーを読む道具（iOS の画面読み上げ・スクリーンリーダー）を持たない利用者には、
 * 誌面を音声で聴く手段が今のところ無い。
 *
 * ここは骨格だけを持つ（手順 1）。操作子・音声選択は手順 2、声が無いときの縮退は手順 3。
 *
 * 不変条件:
 * - **自分からは絶対に喋らない。** `speak()` は利用者の操作からしか呼ばれない
 *   （`1.4.2` の適合根拠「自動再生しない」を守る。Chrome M71 以降と iOS も操作外の `speak()` を拒む）。
 * - **`dispose()` で必ず黙る。** 破棄後は `speak()` を呼んでも何も起きない — ビューワを畳んだ後に
 *   喋り続けるのは、利用者から見て止める手段が無い状態になる。
 * - **句・節の言語に従う**（`3.1.2` で入れた `runs[].lang` の実利用）。区切りごとに発話を分け、
 *   `lang` の付いた区切りはその言語で読む。
 */
import type { DisposeRegistry } from '../core/dispose-registry';
import type { TextSegment } from '../source/text-layer';

/** 使う分だけの `SpeechSynthesis`（jsdom には実体が無いので注入できる形にする）。 */
export interface SpeechSynthesisLike {
  speak(utterance: UtteranceLike): void;
  cancel(): void;
  getVoices(): readonly VoiceLike[];
  readonly speaking: boolean;
  addEventListener(type: 'voiceschanged', handler: () => void): void;
  removeEventListener(type: 'voiceschanged', handler: () => void): void;
}

/** 使う分だけの `SpeechSynthesisUtterance`。 */
export interface UtteranceLike {
  text: string;
  lang: string;
  rate: number;
  voice: VoiceLike | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}

/** 使う分だけの `SpeechSynthesisVoice`。 */
export interface VoiceLike {
  readonly voiceURI: string;
  readonly name: string;
  readonly lang: string;
  readonly default?: boolean;
}

export interface SpeechHost {
  synth: SpeechSynthesisLike;
  /** 発話 1 つを作る（本文は誌面由来なので、生成側で必ず文字列として扱う）。 */
  createUtterance(text: string): UtteranceLike;
}

export interface SpeakOptions {
  /** 区切りに `lang` が無いときの言語（書籍の言語）。 */
  lang: string;
  /** 読み上げ速度。既定 1。 */
  rate?: number;
  /** 使う音声の `voiceURI`。未指定・見つからないときはブラウザ既定に委ねる。 */
  voiceURI?: string | null;
}

/** 読み上げの状態（操作子の見た目は手順 2 でこれに従う）。 */
export type SpeechState = 'idle' | 'speaking';

/** ブラウザが読み上げに対応しているか（対応していなければ操作子自体を出さない）。 */
export function speechHostOf(view: Window & typeof globalThis): SpeechHost | null {
  const synth = view.speechSynthesis as SpeechSynthesisLike | undefined;
  const Utterance = view.SpeechSynthesisUtterance;
  if (!synth || typeof Utterance !== 'function') return null;
  return {
    synth,
    createUtterance: (text) => new Utterance(text) as unknown as UtteranceLike,
  };
}

export class SpeechReader {
  private disposed = false;
  private state: SpeechState = 'idle';
  /** 今回の読み上げで作った発話。中断の判定に使う（他所の発話と取り違えないため）。 */
  private pending: UtteranceLike[] = [];
  private onStateChange: ((state: SpeechState) => void) | null = null;
  private onVoicesChange: (() => void) | null = null;

  constructor(
    private readonly host: SpeechHost,
    registry: DisposeRegistry,
  ) {
    const voicesChanged = (): void => this.onVoicesChange?.();
    this.host.synth.addEventListener('voiceschanged', voicesChanged);
    registry.add('other', () => this.host.synth.removeEventListener('voiceschanged', voicesChanged));
    // 破棄されなくても、ページを離れるときは必ず黙らせる（読み上げは文書を越えて残りうる）。
    registry.add('other', () => this.dispose());
  }

  /** 利用できる音声。`voiceschanged` の前は空になりうる（手順 3 の縮退はそれを見る）。 */
  voices(): readonly VoiceLike[] {
    return this.disposed ? [] : this.host.synth.getVoices();
  }

  /** 音声一覧が入れ替わったときの通知（一覧は遅れて来ることがある）。 */
  watchVoices(handler: () => void): void {
    this.onVoicesChange = handler;
  }

  /** 状態の変化（操作子の名前・状態はこれに従う）。 */
  watchState(handler: (state: SpeechState) => void): void {
    this.onStateChange = handler;
  }

  current(): SpeechState {
    return this.state;
  }

  /**
   * 区切り列を読み上げる。**利用者の操作からのみ呼ぶこと。**
   *
   * 前の読み上げは必ず捨てる（重ねて喋らない）。区切りごとに発話を分けるのは、
   * `lang` の付いた句・節をその言語で読ませるため。
   */
  speak(segments: readonly TextSegment[], options: SpeakOptions): void {
    if (this.disposed) return;
    this.stop();

    const rate = options.rate ?? 1;
    const voices = this.voices();
    const chosen = options.voiceURI
      ? (voices.find((voice) => voice.voiceURI === options.voiceURI) ?? null)
      : null;

    const utterances = segments
      .filter((segment) => segment.text.trim() !== '')
      .map((segment) => {
        const utterance = this.host.createUtterance(segment.text);
        utterance.lang = segment.lang ?? options.lang;
        utterance.rate = rate;
        // 選んだ音声は、その音声の言語に合う区切りにだけ当てる。合わない区切りに当てると
        // 「日本語の声で英文を読む」ことになり、句・節に言語を付けた意味が消える。
        utterance.voice = chosen && sameLanguage(chosen.lang, utterance.lang) ? chosen : null;
        return utterance;
      });

    if (utterances.length === 0) return;

    const last = utterances[utterances.length - 1] as UtteranceLike;
    const finish = (): void => {
      // 自分が出した発話の終わりだけを見る（stop() 後の遅れた通知では戻さない）。
      if (this.pending.includes(last)) this.setState('idle');
    };
    last.onend = finish;
    last.onerror = finish;

    this.pending = utterances;
    this.setState('speaking');
    for (const utterance of utterances) this.host.synth.speak(utterance);
  }

  /** 読み上げをやめる。喋っていないときに呼んでも安全。 */
  stop(): void {
    this.pending = [];
    if (!this.disposed) this.host.synth.cancel();
    this.setState('idle');
  }

  /** 破棄。以後 `speak()` は何もしない（畳んだ後に喋り続けさせない）。 */
  dispose(): void {
    if (this.disposed) return;
    this.host.synth.cancel();
    this.pending = [];
    this.state = 'idle';
    this.disposed = true;
    this.onStateChange = null;
    this.onVoicesChange = null;
  }

  private setState(state: SpeechState): void {
    if (this.state === state) return;
    this.state = state;
    this.onStateChange?.(state);
  }
}

/** 言語タグの先頭サブタグが同じか（`en-US` と `en` を同じ言語として扱う）。 */
function sameLanguage(a: string, b: string): boolean {
  return a.toLowerCase().split('-')[0] === b.toLowerCase().split('-')[0];
}
